import { ChangeDetectionStrategy, Component } from '@angular/core';

import { Store } from '@ngrx/store';
import { Subject } from 'rxjs';
import { first } from 'rxjs/operators';

import { CurrentUserSelectors, UserSelectors } from '@recruitee/account/data';
import { filterBoolean } from '@recruitee/common';
import type { CurrentUser, User } from '@recruitee/http/types';
import { I18nService } from '@recruitee/i18n';
import { DialogComponentI, DialogRef } from '@recruitee/user-interface';

export type AdminsSelectDialogData = Partial<{
  allowedAdmins: User[];
  assignments: number[];
  lockedAssignments: number[];
  lockedAssignmentsTooltip: string;
  heading: string;
  subheading: string;
  preventAdminRemove: boolean;
  showCountBeforeSubheading: boolean;
  minSelectedAdmins: number;
  maxSelectedAdmins: number;
}>;

@Component({
  selector: 'rt-admins-select-dialog',
  templateUrl: './admins-select-dialog.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AdminsSelectDialogComponent
  implements DialogComponentI<AdminsSelectDialogData, User[] | null>
{
  public currentAdmin: CurrentUser;
  public assignments: number[] = [];
  public lockedAssignments: number[] = [];
  public lockedAssignmentsTooltip: string | null = null;
  public heading: string;
  public subheading: string;
  public preventAdminRemove: boolean = false;
  public allowedAdmins: User[] = [];
  public showCountBeforeSubheading: boolean = false;
  public minSelectedAdmins: number = 0;
  public maxSelectedAdmins: number = 0;
  private ngOnDestroy$ = new Subject<void>();

  constructor(private store: Store, private dialog: DialogRef, private i18n: I18nService) {
    this.store
      .select(CurrentUserSelectors.data)
      .pipe(filterBoolean, first())
      .subscribe(currentAdmin => {
        this.currentAdmin = currentAdmin;
      });
  }

  public setDialogData(data: AdminsSelectDialogData) {
    this.assignments = data.assignments || [];
    this.lockedAssignments = data.lockedAssignments || [];
    this.lockedAssignmentsTooltip = data.lockedAssignmentsTooltip || null;
    this.heading = data.heading || this.i18n.translate('app.tasks.assignments.heading');
    this.subheading = data.subheading || this.i18n.translate('app.tasks.assignments.subheading');
    this.preventAdminRemove = data.preventAdminRemove || false;
    this.showCountBeforeSubheading = data.showCountBeforeSubheading || false;
    this.minSelectedAdmins = data.minSelectedAdmins || 0;
    this.maxSelectedAdmins = data.maxSelectedAdmins || 0;

    if (data.allowedAdmins) {
      this.allowedAdmins = data.allowedAdmins;
    } else {
      this.store
        .select(UserSelectors.entities())
        .pipe(first())
        .subscribe(allowedAdmins => (this.allowedAdmins = allowedAdmins || []));
    }
  }

  public ngOnDestroy(): void {
    this.ngOnDestroy$.next();
    this.ngOnDestroy$.complete();
  }

  public cancel(): void {
    this.dialog.close(null);
  }

  public submit(admins: User[]): void {
    this.dialog.close(admins);
  }

  public get currentAdminId(): number {
    return this.currentAdmin.id;
  }
}
